import { useCallback, useRef, useState } from "react";

import API from "@/hooks/api";
import type { MidtermsSummaryItem } from "@/types/requests";

import {
  type County2024,
  DEFAULT_SETTINGS,
  nationalEnvironment,
  OWN_WEIGHTS,
} from "./model";
import { RACES } from "./races";
import { packSample, readSummary, writeSummary } from "./storage";
import {
  NATIONAL_QUESTIONS,
  nationalFigures,
  raceFigures,
  raceKey,
} from "./summary";
import { voteQuestion } from "./survey";
import type {
  Office,
  PublishedSummary,
  SnapshotSamples,
  SnapshotSummaries,
  SnapshotSummariesByWeight,
  StateSample,
} from "./types";
import { buildRows } from "./view";

/* ── Types ─────────────────────────────────────── */

export type PublishProgress =
  | { done: number; step: "writing"; total: number }
  | { step: "preparing" | "saving" };

/** One summary to write, and where it goes in the snapshot. */
type Job = { item: MidtermsSummaryItem; weight: string } & (
  { kind: "national"; office: Office } | { kind: "race"; race: string }
);

/* ── Constants ─────────────────────────────────── */

/* POST /midterms/summaries takes 25 items and 60 requests a minute; one
   request at a time stays well under it. */
const BATCH_SIZE = 25;
const OFFICES: Office[] = ["senate", "governor", "house"];

/* ── Helpers ───────────────────────────────────── */

const nextFrame = () => new Promise((resolve) => setTimeout(resolve, 0));

/** Every summary to publish: each race with a Foresight read and each
    office's national picture, at every own-vote weight. */
const publishJobs = async (
  county2024: County2024,
  edge: number,
  samples: Record<string, StateSample>
): Promise<Job[]> => {
  const jobs: Job[] = [];
  for (const ownWeight of OWN_WEIGHTS) {
    const settings = { ...DEFAULT_SETTINGS, ownWeight };
    const weight = String(ownWeight);
    const environment = nationalEnvironment(samples, {
      county2024,
      edge,
      settings,
    });
    for (const office of OFFICES) {
      const rows = buildRows({
        county2024,
        edge,
        environment,
        external: null,
        layer: "foresight",
        office,
        races: RACES,
        samples,
        settings,
      });
      for (const row of rows) {
        const context = raceFigures({
          county2024,
          edge,
          environment,
          office,
          row,
          settings,
        });
        if (!context) continue;
        const race = raceKey(office, row);
        jobs.push({
          item: {
            context,
            key: `${weight}:${race}`,
            question: voteQuestion(office, row.state),
          },
          kind: "race",
          race,
          weight,
        });
      }
      const context = nationalFigures({
        county2024,
        edge,
        environment,
        office,
        samples,
        settings,
      });
      if (context) {
        jobs.push({
          item: {
            context,
            key: `${weight}:${office}`,
            question: NATIONAL_QUESTIONS[office],
          },
          kind: "national",
          office,
          weight,
        });
      }
    }
    // A weight takes a few hundred milliseconds: let the progress repaint.
    await nextFrame();
  }
  return jobs;
};

/** Same question and figures, same summary (a race read from one
    estimator reads the same at every weight). */
const writtenKey = (item: MidtermsSummaryItem) =>
  `${item.question}\n${item.context}`;

/* ── Hook ──────────────────────────────────────── */

/**
 * Publishes the client view: writes the key insights of every race and of
 * each office's national picture at every own-vote weight, then saves them
 * with the packed samples as the snapshot. Summaries written this session
 * are reused, so a retry resumes where the last attempt stopped.
 */
const usePublish = () => {
  const [failure, setFailure] = useState<"snapshot" | "summaries" | null>(null);
  const [progress, setProgress] = useState<PublishProgress | null>(null);
  const [result, setResult] = useState<{
    /** Summaries still missing after their retry. */
    missing: number;
    publishedAt: string;
  } | null>(null);
  const running = useRef(false);
  const written = useRef(new Map<string, PublishedSummary>());

  const { error: summariesError, generateSummaries } =
    API.Midterms.useGenerateSummaries();
  const { error: snapshotError, updateSnapshot } =
    API.Midterms.useUpdateSnapshot<
      SnapshotSamples,
      SnapshotSummariesByWeight
    >();

  const publish = useCallback(
    async ({
      county2024,
      edge,
      samples,
    }: {
      county2024: County2024;
      /** `turnoutEdge` of the samples. */
      edge: number;
      samples: Record<string, StateSample>;
    }) => {
      if (running.current) return;
      running.current = true;
      setFailure(null);
      setResult(null);
      setProgress({ step: "preparing" });

      /** Each item is tried twice; false when a request failed twice. */
      const writeBatch = async (batch: Job[]): Promise<boolean> => {
        let todo = batch;
        for (let attempt = 1; attempt <= 2 && todo.length > 0; attempt++) {
          const summaries = await generateSummaries(
            todo.map((job) => job.item)
          );
          if (!summaries) {
            if (attempt === 2) return false;
            continue;
          }
          const byKey = new Map(summaries.map((s) => [s.key, s]));
          const missing: Job[] = [];
          for (const job of todo) {
            const found = byKey.get(job.item.key);
            if (!found || found.summary === null) {
              missing.push(job);
              continue;
            }
            const summary = {
              headline: found.headline,
              summary: found.summary,
            };
            written.current.set(writtenKey(job.item), summary);
            if (job.kind === "national") {
              writeSummary(job.item.question, job.item.context, summary);
            }
          }
          todo = missing;
        }
        return true;
      };

      try {
        await nextFrame();
        const jobs = await publishJobs(county2024, edge, samples);
        // National summaries the tracker already wrote in this browser.
        for (const job of jobs) {
          if (job.kind !== "national") continue;
          const saved = readSummary(job.item.question, job.item.context);
          if (saved) written.current.set(writtenKey(job.item), saved);
        }

        const pending = [
          ...new Map(
            jobs
              .filter((job) => !written.current.has(writtenKey(job.item)))
              .map((job) => [writtenKey(job.item), job])
          ).values(),
        ];
        let done = jobs.length - pending.length;
        setProgress({ done, step: "writing", total: jobs.length });
        for (let i = 0; i < pending.length; i += BATCH_SIZE) {
          const batch = pending.slice(i, i + BATCH_SIZE);
          if (!(await writeBatch(batch))) {
            setFailure("summaries");
            return;
          }
          done += batch.length;
          setProgress({ done, step: "writing", total: jobs.length });
        }

        const summaries = Object.fromEntries(
          OWN_WEIGHTS.map((w): [string, SnapshotSummaries] => [
            String(w),
            { national: {}, races: {} },
          ])
        );
        let missing = 0;
        for (const job of jobs) {
          const summary = written.current.get(writtenKey(job.item)) ?? {
            headline: null,
            summary: null,
          };
          if (summary.summary === null) missing++;
          const entry = summaries[job.weight];
          if (job.kind === "national") entry.national[job.office] = summary;
          else entry.races[job.race] = summary;
        }

        setProgress({ step: "saving" });
        const publishedAt = await updateSnapshot({
          published_at: new Date().toISOString(),
          samples: Object.fromEntries(
            Object.entries(samples).map(([state, sample]) => [
              state,
              packSample(sample),
            ])
          ),
          summaries,
        });
        if (publishedAt === null) {
          setFailure("snapshot");
          return;
        }
        setResult({ missing, publishedAt });
      } finally {
        running.current = false;
        setProgress(null);
      }
    },
    [generateSummaries, updateSnapshot]
  );

  const error =
    failure === "summaries"
      ? (summariesError ?? "Couldn't write the summaries.")
      : failure === "snapshot"
        ? (snapshotError ?? "Couldn't save the snapshot.")
        : null;

  return { error, progress, publish, result };
};

export default usePublish;
